import { useHistory } from 'react-router-dom'
import {
  MarketAccessory,
  MarketFooter,
  MarketHeader,
  MarketList,
  MarketRow,
} from '@market/react'
import { ReactComponent as MagnifyingGlass } from '@market/icons/assets/svg/magnifying-glass.svg'
import { ReactComponent as HumanArrowRight } from '@market/icons/assets/svg/human-arrow-right.svg'
import { ReactComponent as Scan } from '@market/icons/assets/svg/scan.svg'
import { RequestContext } from 'utils/pos'
import LoadingScreen from 'components/Loading'
import { useEffect } from 'react'
import { usePosContext } from 'hooks/pos'
import { useGetCustomer } from 'hooks/customer'
import { buildTranslate } from 'locales'
import Switch from 'components/Switch'
import { getOrderDiscount } from 'utils/loyalty'

const t = buildTranslate('main')

const MainScreen = () => {
  const history = useHistory()

  const posContext = usePosContext()
  const { getCustomer, state } = useGetCustomer()

  useEffect(() => {
    async function init() {
      const [customerResult, orderDiscount] = await Promise.all([
        getCustomer(RequestContext.SDK),
        getOrderDiscount(),
      ])

      const redemptionIdentifier = orderDiscount?.metadata?.redemptionId ?? null

      // if there's an applied offer, validate it before going to customer screen
      if (redemptionIdentifier) {
        history.push('/validate-offer' + history.location.search)
        return
      }

      if (customerResult.isOk()) {
        history.push('/customer' + history.location.search)
      }
    }

    init()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getCustomer])

  const isFetchingCustomer = state === 'init' || state === 'pending'

  if (isFetchingCustomer) {
    // Display loading screen until customer has been fetched from SDK.
    return <LoadingScreen />
  }

  const scannerAvailable = !!posContext?.device?.hasScanner

  const enrollmentLinkEnabled =
    !!posContext?.configurations?.enrollmentLinkEnabled

  return (
    <div className="market-grid-container" data-testid="main-screen">
      <MarketHeader className="market-grid-item-full" compact>
        <h2>{t('title')}</h2>
      </MarketHeader>
      <MarketList
        className="market-grid-item-full"
        style={{ marginTop: '16px' }}
      >
        <MarketRow
          data-testid="search-customers-btn"
          onClick={() =>
            history.push('/search-customers' + history.location.search)
          }
          variant="drill"
        >
          <MarketAccessory
            slot="leading-accessory"
            size="image"
            className="addons-market-row-leading-accessory"
          >
            <MagnifyingGlass
              className="addons-main-icon"
              width="24"
              height="24"
            />
          </MarketAccessory>
          <label slot="label">{t('searchCustomers')}</label>
        </MarketRow>

        <Switch condition={enrollmentLinkEnabled}>
          <MarketRow
            data-testid="send-enrollment-link-btn"
            id="last-row"
            onClick={() =>
              history.push('/send-enrollment-link' + history.location.search)
            }
            variant="drill"
          >
            <MarketAccessory
              slot="leading-accessory"
              size="image"
              className="addons-market-row-leading-accessory"
            >
              <HumanArrowRight
                className="addons-main-icon"
                width="24"
                height="24"
              />
            </MarketAccessory>

            <label slot="label">{t('sendEnrollmentLink')}</label>
          </MarketRow>
        </Switch>
      </MarketList>

      <Switch condition={scannerAvailable}>
        <MarketFooter
          className="market-grid-item-full addons-footer-container"
          data-testid="main-footer"
        >
          <div className="addons-footer-content">
            <MarketAccessory size="icon">
              <Scan width="24" height="24" className="addons-scanner-icon" />
            </MarketAccessory>
            <label className="addons-scanner-main-text">
              {t('scannerActivated')}
            </label>
          </div>
          <div className="addons-scanner-helper-text">{t('scanCustomer')}</div>
        </MarketFooter>
      </Switch>
    </div>
  )
}

export default MainScreen
